'use client'

import { ListIcon, PhoneIcon, QuestionIcon, UserCircleIcon, UsersIcon, CaretUpDownIcon } from '@phosphor-icons/react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import React from 'react'

import { Cart } from '@/components/common/Cart/Cart'
import { LoginButton } from '@/components/common/LoginButton/LoginButton'
import { NavigationMenu } from '@/components/common/Navigation/Navigation'
import { Button } from '@/components/primitives/Button/Button'
import { Dropdown } from '@/components/primitives/Dropdown/Dropdown'
import { useCustomer } from '@/context/customer/CustomerContext'
import { useHeaderContext } from '@/context/HeaderContext'
// import { useElementHeight } from '@/hooks/useElementHeight'
// import { useScrollPositionFromTop } from '@/hooks/useScrollPositionFromTop'
import { cn } from '@/lib/utils'

import { Logo } from './Logo'
import { MobileDrawer } from './MobileDrawer'
import { EnquireDrawer } from '../EnquireDrawer/EnquireDrawer'

import type { HeaderEntry } from '@/types/contentful/headerType'

export const HeaderClient = ({ headerData }: { headerData: HeaderEntry }) => {
  const { headerType } = useHeaderContext()
  const pathname = usePathname()
  const { customer, handleBuyerIdentityChange, purchasingCompanyLocation } = useCustomer()
  const locationId = purchasingCompanyLocation?.id

  const { locationOptions, customerState } = customer || {}
  const isOnboarded = customerState === 'ONBOARDED'

  const hasMatchingLocation = locationOptions?.some((option) => option.value === locationId)

  const isAccountPage = pathname?.includes('/account')

  const isSolid = headerType === 'solid'
  const navigation = headerData?.fields?.menu
  const phone = headerData?.fields?.phoneNumber || ''
  const helpLink = headerData?.fields?.helpLink || ''
  const [showOverlay, setShowOverlay] = React.useState(false)
  const enquireFormLink = headerData?.fields?.enquireFormLink

  return (
    <>
      <div
        className={cn(
          'invisible fixed inset-0 z-10 size-full bg-overlay-dark opacity-0 transition-opacity delay-75 ease-in',
          showOverlay ? 'visible opacity-100' : 'pointer-events-none',
        )}
      />
      <header
        data-name="header"
        className={cn(
          'sticky top-0',
          'z-50 h-16 w-full border-b lg:h-20',
          'transition-colors duration-300',
          // hasScrolled && 'sticky top-0',
          // isAccountPage ? 'bg-primary-500' : isSolid ? 'bg-white' : 'absolute inset-x-0 bg-transparent',
          // isSolid ? 'border-grey-200' : 'border-overlay-light',
          isAccountPage ? 'bg-primary-500' : isSolid ? 'bg-white' : 'bg-transparent',
          // isTransparent && (hasScrolled ? 'fixed top-0' : 'absolute inset-x-0'),
        )}
      >
        <div className={cn('space-between flex h-full items-center justify-between px-4 lg:px-12 container:mx-auto container:max-w-content container:px-0 desktop:px-0')}>
          <div className="flex flex-row items-center justify-center gap-4">
            <div className="block container:hidden">
              {!isAccountPage && (
                <MobileDrawer navigation={navigation}>
                  <button aria-label="Open mobile menu" className="flex size-6 items-center justify-center">
                    <ListIcon size={22} className={cn('cursor-pointer', isSolid ? 'text-black' : 'text-white')} />
                  </button>
                </MobileDrawer>
              )}
            </div>
            <div className="flex flex-row items-center justify-center gap-16">
              <Link href="/" aria-label="Supagas home">
                <Logo type={isSolid && !isAccountPage ? 'default' : 'light'} />
              </Link>
              {!isAccountPage && <NavigationMenu navigation={navigation} isSolid={isSolid} setShowOverlay={setShowOverlay} className="hidden container:block" />}
            </div>
          </div>
          <div>
            {customer ? (
              <div className={'flex flex-row items-center justify-center gap-3 container:gap-6'}>
                {helpLink && (
                  <Link
                    href={helpLink}
                    className={cn(
                      'group flex flex-row items-center justify-center gap-2',
                      isAccountPage
                        ? 'text-white container:hover:bg-overlay-light container:focus-visible:bg-overlay-light'
                        : isSolid
                          ? 'text-primary-900 hover:bg-grey-100 focus-visible:bg-grey-100'
                          : 'text-white hover:bg-overlay-light focus-visible:bg-overlay-light',
                      'rounded-full bg-transparent p-2 text-base font-semibold focus-visible:outline-none',
                    )}
                  >
                    {isAccountPage ? (
                      <>
                        <div
                          className={cn(
                            'flex rounded-full bg-overlay-dark p-3 text-white transition-colors container:hidden',
                            'group-hover:bg-overlay-light group-focus-visible:bg-overlay-light',
                          )}
                        >
                          <QuestionIcon size={24} />
                        </div>
                        <QuestionIcon size={24} className="hidden container:flex" />
                      </>
                    ) : (
                      <QuestionIcon size={24} />
                    )}

                    <span className="hidden container:flex">Help</span>
                  </Link>
                )}
                {!isAccountPage && (
                  <Link
                    href="/account"
                    className={cn(
                      'hidden flex-row items-center justify-center gap-2 lg:flex',
                      isSolid ? 'text-primary-900 hover:bg-grey-100' : 'text-white hover:bg-overlay-light',
                      'rounded-full bg-transparent px-4 py-1 text-base font-semibold',
                    )}
                  >
                    <UserCircleIcon size={24} />
                    Account
                  </Link>
                )}
                {isAccountPage &&
                  isOnboarded &&
                  (!!locationOptions?.length && !!locationId && hasMatchingLocation ? (
                    <div className="hidden lg:flex">
                      <Dropdown
                        key={locationId}
                        defaultValue={locationId}
                        triggerClassName={'flex py-3 w-full border-none rounded-lg bg-accent-green-2 text-white container:flex'}
                        icons={{ rightIcon: <UsersIcon size={24} className="min-w-6 text-white" />, leftIcon: <CaretUpDownIcon size={24} className="min-w-6" /> }}
                        options={locationOptions}
                        onChange={handleBuyerIdentityChange}
                      />
                    </div>
                  ) : (
                    // Skeleton Loader for when the locationId is not set/ready
                    <div className="hidden h-12 w-[300px] lg:flex">
                      <div className="relative flex w-full min-w-0 overflow-hidden rounded-lg bg-accent-green-2 py-3">
                        <div className="absolute inset-0 animate-pulse bg-accent-green-3" />
                      </div>
                    </div>
                  ))}
                <div className={cn('flex items-center justify-center gap-3')}>
                  {phone && (
                    <a
                      href={`tel:${phone}`}
                      className={cn(
                        'hidden size-10 rounded-full  border-2 p-2 lg:flex',
                        isSolid ? 'border-primary-500' : 'border-white bg-white',
                        isAccountPage && 'size-12 border-none bg-overlay-dark p-3 text-white',
                      )}
                    >
                      <PhoneIcon size={isAccountPage ? 24 : 20} className={cn('text-primary-500', isAccountPage && 'text-white')} weight={isAccountPage ? 'bold' : 'fill'} />
                    </a>
                  )}
                  <Cart className={isSolid ? 'border-primary-500' : 'border-white bg-white'} />
                </div>
              </div>
            ) : (
              <div className="flex flex-row items-center justify-center gap-3">
                <LoginButton size="small" variant={isSolid ? 'outline' : 'light-outline'} className="hidden lg:flex">
                  Login
                </LoginButton>
                {enquireFormLink && (
                  <EnquireDrawer formSrc={enquireFormLink}>
                    <Button size="small" className="h-8 lg:h-10">
                      Enquire
                    </Button>
                  </EnquireDrawer>
                )}
                {phone && (
                  <a
                    href={`tel:${phone}`}
                    aria-label={`Call ${phone}`}
                    className={cn('hidden size-10 rounded-full  border-2 p-2 lg:flex', isSolid ? 'border-primary-500' : 'border-white bg-white')}
                  >
                    <PhoneIcon size={20} className="text-primary-500" weight="fill" />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  )
}
